import { isPeriodPreset } from "./period-presets";
import {
  EMPTY_FILTERS,
  type FinancialStatusFilter,
  type ItemCountFilter,
  type ItemCountOperator,
  type ItemCountScope,
  type ItemTypeValue,
  type SalesExplorerFilters,
} from "./types";

const ITEM_COUNT_OPERATORS: ItemCountOperator[] = ["gte", "eq", "lte"];
const ITEM_COUNT_SCOPES: ItemCountScope[] = ["all", "products", "services"];
const ITEM_TYPES: ItemTypeValue[] = ["PRODUTO", "SERVICO", "PACOTE"];
const FINANCIAL_STATUSES: FinancialStatusFilter[] = ["all", "paid", "open"];

const setList = (params: URLSearchParams, key: string, values: string[]) => {
  if (values.length > 0) params.set(key, values.join(","));
};

const setText = (params: URLSearchParams, key: string, value: string) => {
  if (value.trim().length > 0) params.set(key, value.trim());
};

const setNumber = (params: URLSearchParams, key: string, value: number | null) => {
  if (value !== null) params.set(key, String(value));
};

export function serializeFilters(filters: SalesExplorerFilters): URLSearchParams {
  const params = new URLSearchParams();

  params.set("period", filters.period);
  setText(params, "from", filters.from);
  setText(params, "to", filters.to);
  setList(params, "appointmentTypeIds", filters.appointmentTypeIds);
  setList(params, "appointmentStatuses", filters.appointmentStatuses);
  setList(params, "professionalIds", filters.professionalIds);
  if (filters.financialStatus !== "all") params.set("financialStatus", filters.financialStatus);
  setList(params, "itemIds", filters.itemIds);
  setList(params, "itemTypes", filters.itemTypes);
  setList(params, "categoryIds", filters.categoryIds);
  if (filters.itemCount) {
    params.set("itemCount", `${filters.itemCount.operator}:${filters.itemCount.value}:${filters.itemCount.scope}`);
  }
  setList(params, "speciesIds", filters.speciesIds);
  setList(params, "breedIds", filters.breedIds);
  setList(params, "sizeCategoryIds", filters.sizeCategoryIds);
  setNumber(params, "weightMin", filters.weightMin);
  setNumber(params, "weightMax", filters.weightMax);
  setText(params, "ownerSearch", filters.ownerSearch);
  setText(params, "ownerCity", filters.ownerCity);
  setText(params, "ownerNeighborhood", filters.ownerNeighborhood);
  if (filters.page > 1) params.set("page", String(filters.page));
  if (filters.pageSize !== EMPTY_FILTERS.pageSize) params.set("pageSize", String(filters.pageSize));

  return params;
}

const readList = (params: URLSearchParams, key: string): string[] => {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(",").filter((value) => value.length > 0);
};

const readNumber = (params: URLSearchParams, key: string): number | null => {
  const raw = params.get(key);
  if (raw === null || raw.trim() === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const readItemCount = (params: URLSearchParams): ItemCountFilter | null => {
  const raw = params.get("itemCount");
  if (!raw) return null;

  const [operator, value, scope] = raw.split(":");
  const parsedValue = Number(value);

  if (!ITEM_COUNT_OPERATORS.includes(operator as ItemCountOperator)) return null;
  if (!Number.isFinite(parsedValue)) return null;
  if (!ITEM_COUNT_SCOPES.includes(scope as ItemCountScope)) return null;

  return { operator: operator as ItemCountOperator, scope: scope as ItemCountScope, value: parsedValue };
};

export function parseFilters(
  params: URLSearchParams,
  fallback: SalesExplorerFilters,
): SalesExplorerFilters {
  const financialStatus = params.get("financialStatus");
  const page = readNumber(params, "page");
  const pageSize = readNumber(params, "pageSize");

  const period = params.get("period");

  return {
    period: isPeriodPreset(period) ? period : fallback.period,
    from: params.get("from") ?? fallback.from,
    to: params.get("to") ?? fallback.to,
    appointmentTypeIds: readList(params, "appointmentTypeIds"),
    appointmentStatuses: readList(params, "appointmentStatuses"),
    professionalIds: readList(params, "professionalIds"),
    financialStatus: FINANCIAL_STATUSES.includes(financialStatus as FinancialStatusFilter)
      ? (financialStatus as FinancialStatusFilter)
      : "all",
    itemIds: readList(params, "itemIds"),
    itemTypes: readList(params, "itemTypes").filter((value): value is ItemTypeValue =>
      ITEM_TYPES.includes(value as ItemTypeValue),
    ),
    categoryIds: readList(params, "categoryIds"),
    itemCount: readItemCount(params),
    speciesIds: readList(params, "speciesIds"),
    breedIds: readList(params, "breedIds"),
    sizeCategoryIds: readList(params, "sizeCategoryIds"),
    weightMin: readNumber(params, "weightMin"),
    weightMax: readNumber(params, "weightMax"),
    ownerSearch: params.get("ownerSearch") ?? "",
    ownerCity: params.get("ownerCity") ?? "",
    ownerNeighborhood: params.get("ownerNeighborhood") ?? "",
    page: page !== null && page >= 1 ? page : 1,
    pageSize: pageSize !== null && pageSize >= 1 ? pageSize : fallback.pageSize,
  };
}
